import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Language } from 'configs/Language'
import { message } from 'antd'
import ArticlesService from 'services/ArticlesService'

export const initialState = {
	loading: false,
	loadingSidePanel: false,
	loadingAllCategories: false,
	sidePanelArticles: [],
	articlesList: [],
	articleFilters: [],
	articleCategoriesList: [],
	allCategoriesList: [],
	articleSectionsList: [],
	articlePagination: {
		from: 1,
		to: 1,
		perPage: 1,
		total: 0,
		currentPage: 1,
		prevPage: null,
		nextPage: null,
		lastPage: 0,
	},
	articleGalleryLoaded: false,
	articleGalleryData: {
		selectedTemplateId: null,
		selectedTemplateRows: null,
		templateList: [],
	},
	articleGalleryContent: [],
	articleGalleryOrders: [],
	articleData: null,
	selectedLang: Language.ALL,
}

export const getArticleList = createAsyncThunk('articles', async (data, { rejectWithValue }) => {
	try {
		const response = await ArticlesService.getArticles(data)
		return response.result
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const getArticleGallery = createAsyncThunk(
	'articles/gallery',
	async (data, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.getArticles(data)
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const saveArticleGallery = createAsyncThunk(
	'articles/gallery/save',
	async (data, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.saveArticleGallery(data)
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const addOrUpdateArticle = createAsyncThunk(
	'articles/add',
	async (data, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.addOrUpdateArticle(data)
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const getArticle = createAsyncThunk(
	'articles/fetchById',
	async ({ articleId }, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.getArticle(articleId)
			return response.result
		} catch (err) {
			console.error('Error in getArticle:', err)
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const addCategory = createAsyncThunk(
	'articles/categories/add',
	async ({ name, language, linkedCategories }, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.addCategory({ name, language, linkedCategories })
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const updateCategory = createAsyncThunk(
	'articles/categories/update',
	async ({ id, name, language, linkedCategories }, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.updateCategory({ id, name, language, linkedCategories })
			return response
		} catch (err) {
			console.error('updateCategory error:', err)
			return rejectWithValue(err.response?.data?.message || 'Failed to update category')
		}
	}
)

export const deleteCategory = createAsyncThunk(
	'articles/categories/delete',
	async (id, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.deleteCategory(id)
			return { id }
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const getArticleCategories = createAsyncThunk(
	'articles/categories',
	async (params = {}, { rejectWithValue, getState }) => {
		const { selectedLang } = getState().articles
		const requestParams = {
			lang: params.lang || params.selectedLanguage || selectedLang,
			search: params.search || '',
			page: params.page || 1,
			perPage: params.perPage || 30,
		}

		try {
			const response = await ArticlesService.getArticleCategories(requestParams)
			return {
				categories: response.result,
				pagination: response.pagination || { total: response.result.length },
			}
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const getAllArticleCategories = createAsyncThunk(
	'articles/allCategories',
	async (_, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.getArticleCategories({})
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const deleteArticle = createAsyncThunk(
	'articles/delete',
	async (articleId, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.deleteArticle(articleId)
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const saveSidePanelArticles = createAsyncThunk(
	'articles/side-panel/save',
	async (data, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.saveSidePanelArticles(data)
			return response
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const getSidePanelArticles = createAsyncThunk(
	'articles/side-panel/get',
	async ({ lang }, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.getSidePanelArticles({ lang })
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const getArticleSections = createAsyncThunk(
	'articles/sections',
	async (lang, { rejectWithValue }) => {
		try {
			const response = await ArticlesService.getArticleSections({ lang })
			return response.result
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)

export const articlesSlice = createSlice({
	name: 'articles',
	initialState,
	reducers: {
		updateArticleList: (state, action) => {
			const { articles, orders } = action.payload
			state.articleGalleryContent = articles
			state.articleGalleryOrders = orders
		},
		showLoading: (state) => {
			state.loading = true
		},
		disableLoading: (state) => {
			state.loading = false
		},
		setSelectedLang: (state, action) => {
			state.selectedLang = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			// Add or update articles
			.addCase(addOrUpdateArticle.pending, (state) => {
				state.loading = true
			})
			.addCase(addOrUpdateArticle.fulfilled, (state, action) => {
				state.loading = false
				message.success(action.payload.message)
			})
			.addCase(addOrUpdateArticle.rejected, (state, action) => {
				state.loading = false
			})
			// List
			.addCase(getArticleList.pending, (state) => {
				state.articlesList = []
				state.loading = true
			})
			.addCase(getArticleList.fulfilled, (state, action) => {
				state.loading = false
				state.articlesList = action.payload.data
				state.articleFilters = action.payload.filters
				state.articlePagination = action.payload.pagination
			})
			.addCase(getArticleList.rejected, (state) => {
				state.loading = false
			})
			// Single article
			.addCase(getArticle.pending, (state) => {
				state.loading = true
			})
			.addCase(getArticle.fulfilled, (state, action) => {
				state.loading = false
				state.articleData = action.payload
			})
			.addCase(getArticle.rejected, (state) => {
				state.loading = false
			})
			// Delete article
			.addCase(deleteArticle.pending, (state) => {
				state.loading = true
			})
			.addCase(deleteArticle.fulfilled, (state, action) => {
				state.loading = false
				message.success(action.payload.message)
			})
			.addCase(deleteArticle.rejected, (state, action) => {
				state.loading = false
			})
			// Fetch categories list
			.addCase(getArticleCategories.pending, (state) => {
				state.articleCategoriesList = []
				state.loading = true
			})
			.addCase(getArticleCategories.fulfilled, (state, action) => {
				state.loading = false
				state.articleCategoriesList = action.payload.categories
				state.articlePagination = action.payload.pagination
			})
			.addCase(getArticleCategories.rejected, (state, action) => {
				state.loading = false
			})
			// Fetch all categories
			.addCase(getAllArticleCategories.pending, (state) => {
				state.loadingAllCategories = true
			})
			.addCase(getAllArticleCategories.fulfilled, (state, action) => {
				state.loadingAllCategories = false
				state.allCategoriesList = action.payload
			})
			.addCase(getAllArticleCategories.rejected, (state, action) => {
				state.loadingAllCategories = false
			})
			// Add new category
			.addCase(addCategory.pending, (state) => {
				state.loading = true
			})
			.addCase(addCategory.fulfilled, (state, action) => {
				state.loading = false
				state.articleCategoriesList.push(action.payload.result)
				state.allCategoriesList.push(action.payload.result)
				message.success(action.payload.message)
			})
			.addCase(addCategory.rejected, (state, action) => {
				state.loading = false
			})
			// Update category
			.addCase(updateCategory.pending, (state) => {
				state.loading = true
			})
			.addCase(updateCategory.fulfilled, (state, action) => {
				state.loading = false
				const index = state.articleCategoriesList.findIndex(
					(cat) => cat.id === action.payload.result.id
				)
				if (index !== -1) {
					state.articleCategoriesList[index] = action.payload.result
				}
				const allIndex = state.allCategoriesList.findIndex((cat) => cat.id === action.payload.result.id)
				if (allIndex !== -1) {
					state.allCategoriesList[allIndex] = action.payload.result
				}
			})
			.addCase(updateCategory.rejected, (state, action) => {
				console.error('updateCategory rejected:', action.payload)
				state.loading = false
			})
			// Delete category
			.addCase(deleteCategory.pending, (state) => {
				state.loading = true
			})
			.addCase(deleteCategory.fulfilled, (state, action) => {
				state.loading = false
				state.articleCategoriesList = state.articleCategoriesList.filter(
					(cat) => cat.id !== action.payload.id
				)
				state.allCategoriesList = state.allCategoriesList.filter((cat) => cat.id !== action.payload.id)
			})
			.addCase(deleteCategory.rejected, (state, action) => {
				console.error('deleteCategory rejected:', action.payload)
				state.loading = false
			})
			// Fetch sections list
			.addCase(getArticleSections.pending, (state) => {
				state.articleSectionsList = []
				state.loading = true
			})
			.addCase(getArticleSections.fulfilled, (state, action) => {
				state.loading = false
				state.articleSectionsList = action.payload
			})
			.addCase(getArticleSections.rejected, (state) => {
				state.loading = false
			})
			// Article gallery
			.addCase(getArticleGallery.pending, (state) => {
				state.articleGalleryLoaded = false
				state.loading = true
			})
			.addCase(getArticleGallery.fulfilled, (state, action) => {
				state.articleGalleryContent = action.payload.data
				state.articleGalleryOrders = []
				state.articleGalleryLoaded = true
				state.loading = false
			})
			.addCase(getArticleGallery.rejected, (state) => {
				state.loading = false
			})
			// Save article gallery
			.addCase(saveArticleGallery.pending, (state) => {
				state.loading = true
			})
			.addCase(saveArticleGallery.fulfilled, (state, action) => {
				state.articleGalleryOrders = []
				state.loading = false
			})
			.addCase(saveArticleGallery.rejected, (state, action) => {
				state.loading = false
			})
			// Save side panel articles
			.addCase(saveSidePanelArticles.pending, (state) => {
				state.loadingSidePanel = true
			})
			.addCase(saveSidePanelArticles.fulfilled, (state, action) => {
				state.loadingSidePanel = false
				if (action.payload && action.payload.message) {
					message.success(action.payload.message)
				}
			})
			.addCase(saveSidePanelArticles.rejected, (state, action) => {
				state.loadingSidePanel = false
			})
			// Get side panel articles
			.addCase(getSidePanelArticles.pending, (state) => {
				state.loadingSidePanel = true
			})
			.addCase(getSidePanelArticles.fulfilled, (state, action) => {
				state.loadingSidePanel = false
				state.sidePanelArticles = action.payload
			})
			.addCase(getSidePanelArticles.rejected, (state) => {
				state.loadingSidePanel = false
				state.sidePanelArticles = []
			})
	},
})

export const { showLoading, disableLoading, updateArticleList, setSelectedLang } =
	articlesSlice.actions

export default articlesSlice.reducer
